import type { FC, MutableRefObject } from 'react';
import React, { useCallback, useEffect, useState } from 'react';

import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import AnalyticsListener from '@atlaskit/analytics-next/AnalyticsListener';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';

import { fg } from '@confluence/feature-gating';
import { getUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId.ts';

/**
 * A mutable ref that `RendererTTIMarker` populates with a "the Atlaskit renderer has finished a
 * render pass" notifier. Consumers create the ref once (`useRef(null)`, or an instance field on a
 * class component), pass it in, and call `ref.current?.()` from the renderer's `onRendered`
 * callback.
 *
 * The indirection is deliberate: it keeps the post-paint readiness state *inside*
 * `RendererTTIMarker`. Holding that state in a consumer re-renders the entire renderer subtree
 * after paint, which lands inside the VC90 measurement window and measurably regresses it — see
 * COPPER-1053. Because `children` is passed in as an element, a re-render of `RendererTTIMarker`
 * alone lets React bail out on the whole document subtree.
 */
export type RendererRenderedSignalRef = MutableRefObject<(() => void) | null>;

/**
 * This component is wrapped around the TWP Atlaskit Fabric Renderer to detect when the renderer
 * has finished rendering the current revision. When it has, this component renders the results of
 * renderWhenReady() and releases its UFO load hold.
 *
 * Readiness has two possible sources, both recorded in this component's own state so that neither
 * costs more to flip than the other:
 *
 * - the `renderer` `rendered` analytics event — the original source, always honoured.
 * - `renderedSignalRef` — driven by the renderer's `onRendered` lifecycle callback, which fires on
 *   every render pass regardless of analytics sampling.
 *
 * The second source is honoured behind `platform_renderer_sample_high_volume_events` — the same gate
 * that samples the event. That is deliberate rather than incidental: the gate being enabled is
 * exactly the condition under which the event stops being a reliable readiness signal, so one gate
 * answers both "is the event sampled?" and "do I need the unsampled callback?". There is no
 * combination of settings that leaves the hold waiting on a signal that will not arrive.
 *
 * Both branches must keep readiness in local state. Hoisting it into a consumer re-renders the whole
 * renderer subtree after paint, inside the VC90 measurement window — and doing so in only one branch
 * makes the two gate cohorts differ in render cost, which is what regressed Edit Page VC90 by 182 ms
 * in COPPER-1053.
 */
export const RendererTTIMarker: FC<{
	revision?: string;
	renderWhenReady: () => React.ReactNode;
	children?: React.ReactNode;
	/**
	 * Populated by this component with a notifier to call from the renderer's `onRendered`
	 * callback. See {@link RendererRenderedSignalRef}.
	 */
	renderedSignalRef?: RendererRenderedSignalRef;
	/**
	 * Used only to reset recorded readiness when the rendered content changes. `revision` values
	 * are per-content version identifiers and can repeat across different content, so without this
	 * a revision recorded for previously-rendered content could satisfy `isReady` immediately for
	 * new content and release the hold before the renderer has run.
	 */
	contentId?: string;
}> = ({ renderWhenReady, revision = null, children, renderedSignalRef, contentId }) => {
	const [currentRenderedRevision, setCurrentRenderedRevision] = useState<string | null>(null);
	const [renderedRevision, setRenderedRevision] = useState<string | null>(null);

	// Hand the consumer a notifier that records the revision current at the moment the renderer
	// reports completion. Kept in this component so the resulting re-render does not reach children.
	//
	// The gate is read here, inside the notifier, rather than in `isReady` below. With the gate
	// disabled the unsampled analytics event is the readiness signal, so recording state here would
	// trigger a re-render that cannot change the outcome — and an update in only one arm is exactly
	// the cost asymmetry this component exists to avoid. Reading the gate at this point also keeps
	// exposure tied to "the renderer reported completion", which happens at the same rate in both
	// cohorts; reading it in the effect body instead would fire exposure on mount, before the gate
	// decides anything, and would count sessions that never complete a render.
	useEffect(() => {
		if (!renderedSignalRef) {
			return;
		}
		renderedSignalRef.current = () => {
			if (fg('platform_renderer_sample_high_volume_events')) {
				setRenderedRevision(revision);
			}
		};
		return () => {
			renderedSignalRef.current = null;
		};
	}, [renderedSignalRef, revision]);

	useEffect(() => {
		setRenderedRevision(null);
		setCurrentRenderedRevision(null);
	}, [contentId]);

	const handleEvent = useCallback(
		(event: UIAnalyticsEvent) => {
			if (event.payload.action === 'rendered' && event.payload.actionSubject === 'renderer') {
				// Store the current revision so we know what content just rendered
				setCurrentRenderedRevision(revision);

				// adding uniquePageLoad info to renderer rendered event
				event.update((payload) => {
					return {
						...payload,
						attributes: {
							...payload.attributes,
							pageLoadInfo: getUniquePageLoadId(),
						},
					};
				});
			}
		},
		[revision, setCurrentRenderedRevision],
	);

	// The content revision prop updates earlier than the renderer signals completion.
	// We aren't "ready" until the current revision has rendered. No gate check is needed here:
	// `renderedRevision` is only ever recorded when the gate is enabled (see the notifier above).
	const isReady =
		revision !== null && (renderedRevision === revision || currentRenderedRevision === revision);
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const shouldInvokeRenderWhenReady = isReady || process.env.REACT_SSR;

	return (
		<UFOLoadHold name="RendererTTIMarker" hold={!shouldInvokeRenderWhenReady}>
			<AnalyticsListener channel="editor" onEvent={handleEvent}>
				{children}
				{shouldInvokeRenderWhenReady && renderWhenReady()}
			</AnalyticsListener>
		</UFOLoadHold>
	);
};
